import { useGlobalMutation } from 'state/query-client/hooks';
import { 
    deleteDatabaseConnection, 
    DeleteDatabaseConnectionResponse 
} from 'services/database-connection-service';
import { useConfig } from './use-config';

/**
 * Custom hook to delete database connections
 *
 * This hook provides a mutation function to delete database connections.
 * It uses the global mutation pattern for consistent error handling and authentication management.
 *
 * Features:
 * - Automatic configuration injection
 * - Global error handling with authentication management
 * - Loading state management
 * - TypeScript support for request/response types
 *
 * @returns {Object} The mutation object with methods and state
 * @returns {Function} mutate - Function to trigger the database connection deletion
 * @returns {Function} mutateAsync - Async function to trigger the database connection deletion
 * @returns {boolean} isPending - Whether the mutation is in progress
 * @returns {boolean} isSuccess - Whether the mutation completed successfully
 * @returns {boolean} isError - Whether the mutation failed
 * @returns {DeleteDatabaseConnectionResponse | undefined} data - The delete response data
 * @returns {Error | null} error - The error if mutation failed
 *
 * @example
 * const { mutate: deleteConnection, isPending } = useDeleteDatabaseConnection();
 * 
 * const handleDeleteConnection = (connectionId) => {
 *   deleteConnection(connectionId, {
 *     onSuccess: (data) => {
 *       console.log('Connection deleted:', data);
 *     },
 *     onError: (error) => {
 *       console.error('Failed to delete connection:', error);
 *     }
 *   });
 * };
 */
export const useDeleteDatabaseConnection = () => {
    const { configurationSettings } = useConfig();

    return useGlobalMutation<DeleteDatabaseConnectionResponse, Error, string>({
        mutationFn: async (connectionId: string) => {
            if (!configurationSettings) {
                throw new Error('Configuration settings are not available');
            }
            return await deleteDatabaseConnection(configurationSettings, connectionId);
        },
    });
};

